import { useEffect, useRef, useState } from "react";
import { LogOut, ChevronDown, Search, Eye } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePageTitle } from "@/lib/title";
import { useNavigate, useLocation } from "react-router-dom";
import { ImpersonateUserModal } from "@/components/layout/ImpersonateUserModal";

const defaultRouteTitles: Record<string, string> = {
  "/": "Dashboard",
  "/courses": "Courses",
  "/my-learning": "My Learning",
  "/certificates": "Certificates",
  "/categories": "Categories",
  "/users": "Users",
  "/analytics": "Analytics",
  "/audit-logs": "Audit Logs",
  "/notifications": "Notifications",
};

export function Topbar() {
  const { user, logout } = useAuth();
  const { title, searchValue, onSearchChange, searchPlaceholder, action } = usePageTitle();
  const [open, setOpen] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const displayTitle = title || defaultRouteTitles[location.pathname] || "ZKI LMS";

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  return (
    <header className="flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-6">
      <div className="flex min-w-0 shrink-0 items-center gap-3">
        <h1 className="truncate text-sm font-semibold text-slate-500">{displayTitle}</h1>
      </div>

      <div className="flex flex-1 justify-center">
        {onSearchChange && (
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder={searchPlaceholder || "Search..."}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-3 text-sm focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 transition-all"
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {action}

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${open ? "bg-slate-100" : "hover:bg-slate-100"}`}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
              {user?.full_name?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div className="text-left">
              <p className="font-medium leading-tight text-slate-800">{user?.full_name}</p>
              <p className="text-xs capitalize leading-tight text-slate-500">{user?.role}</p>
            </div>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-xl border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
                  {user?.full_name?.[0]?.toUpperCase() ?? "?"}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{user?.full_name}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>

              {user?.role === "admin" && (
                <div className="py-1">
                  <button
                    onClick={() => {
                      setOpen(false);
                      setShowImpersonate(true);
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Eye className="h-4 w-4 text-slate-400" /> View as user
                  </button>
                </div>
              )}

              <div className="border-t border-slate-100 py-1">
                <button
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showImpersonate && <ImpersonateUserModal onClose={() => setShowImpersonate(false)} />}
    </header>
  );
}
